import React, { useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { NAME_MAX_LENGTH, enforceNameConstraints, checkSpecialCharacters } from '../../../../lib/validation';
import { showToastMessage, preventKeyInputs } from '../../../../../../../utils/commonFunctions';
import styles from './GeoFeaturePanel.module.scss';
import { geodeticToGeocentric, geocentricToGeodetic, geodeticToEcef, ecefToGeodetic } from '../../../../lib/coordinates';

const limitDecimalPlaces = (valStr: string, maxDecimals: number = 8): string => {
  if (!valStr.includes('.')) return valStr;
  const parts = valStr.split('.');
  if (parts[1].length > maxDecimals) {
    return `${parts[0]}.${parts[1].substring(0, maxDecimals)}`;
  }
  return valStr;
};

interface Props {
  name: string;
  onNameChange: (v: string) => void;
  lat: number | null;
  lng: number | null;
  onCoordsChange: (lat: number, lng: number) => void;
  isDrawing: boolean;
  // Lifted coordinate metadata
  coordSystem: string;
  onCoordSystemChange: (v: string) => void;
  altDatum: 'MSL' | 'WGS84';
  onAltDatumChange: (v: 'MSL' | 'WGS84') => void;
  altUnit: 'km' | 'm';
  onAltUnitChange: (v: 'km' | 'm') => void;
  altValue: string;
  onAltValueChange: (v: string) => void;
  xVal: string;
  onXChange: (v: string) => void;
  yVal: string;
  onYChange: (v: string) => void;
  zVal: string;
  onZChange: (v: string) => void;
}

export { geodeticToGeocentric, geocentricToGeodetic, geodeticToEcef, ecefToGeodetic };



const COORD_SYSTEM_OPTIONS = [
  { value: 'geodetic', label: 'Geodetic' },
  { value: 'geocentric', label: 'Geocentric' },
  { value: 'cartesian', label: 'Cartesian' },
];

const ChevronDown = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const TargetBody: React.FC<Props> = ({
  name,
  onNameChange,
  lat,
  lng,
  onCoordsChange,
  coordSystem,
  onCoordSystemChange,
  altDatum,
  onAltDatumChange,
  altUnit,
  onAltUnitChange,
  altValue,
  onAltValueChange,
  xVal,
  onXChange,
  yVal,
  onYChange,
  zVal,
  onZChange,
}) => {
  const [coordOpen, setCoordOpen] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  const handleCoordTriggerClick = () => {
    if (!coordOpen && triggerRef.current) {
      setTriggerRect(triggerRef.current.getBoundingClientRect());
    }
    setCoordOpen(o => !o);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !dropRef.current?.contains(t)) {
        setCoordOpen(false);
      }
    };
    if (coordOpen) document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [coordOpen]);

  const handleLatKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!preventKeyInputs(e, ['e', 'E', '+'])) return;
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const currentDispLat = coordSystem === 'geocentric' && lat != null && !isNaN(lat)
        ? geodeticToGeocentric(lat)
        : (lat ?? 0);
      let step = 1;
      if (e.shiftKey) step = 10;
      else if (e.altKey || e.ctrlKey) step = 0.1;

      const newDispLat = e.key === 'ArrowUp' ? currentDispLat + step : currentDispLat - step;
      if (newDispLat >= -90 && newDispLat <= 90) {
        const geodeticLat = coordSystem === 'geocentric' ? geocentricToGeodetic(newDispLat) : newDispLat;
        onCoordsChange(parseFloat(geodeticLat.toFixed(4)), lng ?? 0);
      } else {
        showToastMessage('info', 'Latitude must be between -90 and 90°');
      }
    }
  };

  const handleLngKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!preventKeyInputs(e, ['e', 'E', '+'])) return;
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const numValue = lng ?? 0;
      let step = 1;
      if (e.shiftKey) step = 10;
      else if (e.altKey || e.ctrlKey) step = 0.1;

      const newValue = e.key === 'ArrowUp' ? numValue + step : numValue - step;
      if (newValue >= -180 && newValue <= 180) {
        onCoordsChange(lat ?? 0, parseFloat(newValue.toFixed(4)));
      } else {
        showToastMessage('info', 'Longitude must be between -180 and 180°');
      }
    }
  };

  const handleCartesianKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    valueKm: string,
    onChangeKm: (v: string) => void
  ) => {
    if (!preventKeyInputs(e, ['e', 'E', '+'])) return;
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const val = parseFloat(valueKm) || 0;
      let step = 1;
      if (e.shiftKey) step = 10;
      else if (e.altKey || e.ctrlKey) step = 0.1;

      const newVal = e.key === 'ArrowUp' ? val + step : val - step;
      onChangeKm(parseFloat(newVal.toFixed(4)).toString());
    }
  };

  const handleLat = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawVal = e.target.value;
    if (rawVal === '' || rawVal === '-') {
      onCoordsChange(NaN, (lng != null && !isNaN(lng)) ? lng : NaN);
      return;
    }
    const limited = limitDecimalPlaces(rawVal);
    const v = parseFloat(limited);
    if (!isNaN(v)) {
      if (v < -90 || v > 90) {
        showToastMessage('info', 'Latitude must be between -90 and 90°');
        return;
      }
      const geodeticLat = coordSystem === 'geocentric' ? geocentricToGeodetic(v) : v;
      onCoordsChange(parseFloat(geodeticLat.toFixed(8)), (lng != null && !isNaN(lng)) ? lng : NaN);
    }
  };

  const handleLng = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawVal = e.target.value;
    if (rawVal === '' || rawVal === '-') {
      onCoordsChange((lat != null && !isNaN(lat)) ? lat : NaN, NaN);
      return;
    }
    const limited = limitDecimalPlaces(rawVal);
    const v = parseFloat(limited);
    if (!isNaN(v)) {
      if (v < -180 || v > 180) {
        showToastMessage('info', 'Longitude must be between -180 and 180°');
        return;
      }
      onCoordsChange((lat != null && !isNaN(lat)) ? lat : NaN, parseFloat(v.toFixed(8)));
    }
  };

  const handleUnitClick = () => {
    const newUnit = altUnit === 'km' ? 'm' : 'km';
    if (altValue !== '') {
      const num = parseFloat(altValue);
      if (!isNaN(num)) {
        onAltValueChange(
          newUnit === 'm'
            ? String(+(num * 1000).toFixed(3))
            : String(+(num / 1000).toFixed(6))
        );
      }
    }
    onAltUnitChange(newUnit);
  };

  const currentCoordLabel = COORD_SYSTEM_OPTIONS.find(o => o.value === coordSystem)?.label ?? 'Geodetic';
  const isCartesian = coordSystem === 'cartesian';
  const displayLat = coordSystem === 'geocentric' && lat != null && !isNaN(lat)
    ? parseFloat(geodeticToGeocentric(lat).toFixed(4))
    : lat;

  // Display meters to user (Figma UX) but store km in parent/API state
  const displayToKm = (metersStr: string): string => {
    const meters = parseFloat(metersStr);
    if (isNaN(meters)) return '';
    return String(meters / 1000);
  };

  const kmToDisplay = (kmStr: string): string => {
    const km = parseFloat(kmStr);
    if (isNaN(km)) return '';
    return String(+(km * 1000).toFixed(3));
  };

  return (
    <>
      {/* Standalone Name Field with separator border */}
      <div className={styles.nameRow}>
        <span className={styles.lbl}>Name</span>
        <div className={`${styles.input} ${styles.text}`}>
          <input
            value={name}
            maxLength={NAME_MAX_LENGTH}
            onChange={e => {
              const inputValue = e.target.value;
              const enforced = enforceNameConstraints(inputValue);
              onNameChange(enforced);

              // Check if character limit reached
              if (enforced.length === NAME_MAX_LENGTH) {
                showToastMessage('info', `Character limit of ${NAME_MAX_LENGTH} reached`);
              }

              // Check if special characters were rejected
              if (inputValue !== enforced && inputValue.length > enforced.length) {
                const specialCharError = checkSpecialCharacters(inputValue);
                if (specialCharError) {
                  showToastMessage('info', specialCharError);
                }
              }
            }}
            placeholder="Point_01"
          />
        </div>
      </div>

      {/* Geofeature Details header with coordinate select selector on the right */}
      <div className={styles.detailsHeader}>
        <span className={styles.detailsTitle}>Geofeature Details</span>
        <span
          ref={triggerRef}
          className={styles.detailsTrigger}
          onClick={handleCoordTriggerClick}
        >
          {currentCoordLabel}
          <ChevronDown />
        </span>
        {coordOpen && triggerRect && ReactDOM.createPortal(
          <div
            ref={dropRef}
            className={styles.dropdownPop}
            style={{
              position: 'fixed',
              top: triggerRect.bottom + 4,
              left: triggerRect.right - 140, // align right to trigger
              width: 140,
              zIndex: 9999,
            }}
            onClick={e => e.stopPropagation()}
          >
            {COORD_SYSTEM_OPTIONS.map(o => (
              <div
                key={o.value}
                className={`${styles.dropdownOpt} ${coordSystem === o.value ? styles.dropdownOptActive : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onCoordSystemChange(o.value);
                  setCoordOpen(false);
                }}
              >
                {o.label}
              </div>
            ))}
          </div>,
          document.body
        )}
      </div>

      {isCartesian ? (
        <div className={styles.coordFieldsList}>
          {/* Position X */}
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>Position X</span>
            <div className={styles.segmentedInputGroup}>
              <input
                type="number"
                step="0.001"
                placeholder="x"
                value={xVal}
                onKeyDown={e => handleCartesianKeyDown(e, xVal, onXChange)}
                onChange={e => onXChange(limitDecimalPlaces(e.target.value))}
                className={styles.segmentInput}
              />
              <span className={styles.segmentUnit}>km</span>
            </div>
          </div>

          {/* Position Y */}
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>Position Y</span>
            <div className={styles.segmentedInputGroup}>
              <input
                type="number"
                step="0.001"
                placeholder="y"
                value={yVal}
                onKeyDown={e => handleCartesianKeyDown(e, yVal, onYChange)}
                onChange={e => onYChange(limitDecimalPlaces(e.target.value))}
                className={styles.segmentInput}
              />
              <span className={styles.segmentUnit}>km</span>
            </div>
          </div>

          {/* Position Z */}
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>Position Z</span>
            <div className={styles.segmentedInputGroup}>
              <input
                type="number"
                step="0.001"
                placeholder="z"
                value={zVal}
                onKeyDown={e => handleCartesianKeyDown(e, zVal, onZChange)}
                onChange={e => onZChange(limitDecimalPlaces(e.target.value))}
                className={styles.segmentInput}
              />
              <span className={styles.segmentUnit}>km</span>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.coordFieldsList}>
          {/* Latitude */}
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>Latitude</span>
            <div className={styles.segmentedInputGroup}>
              <input
                type="number"
                step="0.0001"
                placeholder="0.0000"
                value={displayLat != null && !isNaN(displayLat) ? displayLat : ''}
                onKeyDown={handleLatKeyDown}
                onChange={handleLat}
                className={styles.segmentInput}
              />
              <span className={styles.segmentUnit}>°</span>
            </div>
          </div>

          {/* Longitude */}
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>Longitude</span>
            <div className={styles.segmentedInputGroup}>
              <input
                type="number"
                step="0.0001"
                placeholder="0.0000"
                value={lng != null && !isNaN(lng) ? lng : ''}
                onKeyDown={handleLngKeyDown}
                onChange={handleLng}
                className={styles.segmentInput}
              />
              <span className={styles.segmentUnit}>°</span>
            </div>
          </div>

          {/* Altitude */}
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>Altitude</span>
            <div className={styles.threeSegmentInput}>
              <span
                className={styles.segmentToggleLeft}
                onClick={() => onAltDatumChange(altDatum === 'MSL' ? 'WGS84' : 'MSL')}
                title="Click to switch datum"
              >
                {altDatum}
              </span>
              <input
                type="number"
                step={altUnit === 'km' ? '0.001' : '1'}
                placeholder={altUnit === 'km' ? '0.000' : '0'}
                value={altValue}
                onKeyDown={e => preventKeyInputs(e, ['e', 'E', '+', '-'])}
                onChange={e => {
                  const v = limitDecimalPlaces(e.target.value);
                  const num = parseFloat(v);
                  const maxAlt = altUnit === 'km' ? 10 : 10000;
                  if (!isNaN(num) && (num < 0 || num > maxAlt)) {
                    showToastMessage('info', `Altitude must be between ${0} and ${maxAlt} ${altUnit}`);
                    return;
                  }
                  onAltValueChange(v);
                }}
                className={styles.segmentInputMiddle}
              />
              <span
                className={styles.segmentToggleRight}
                onClick={handleUnitClick}
                title="Click to switch unit"
              >
                {altUnit === 'km' ? 'Km' : 'm'}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TargetBody;
